import React, { useState } from "react";
import { useRouter } from "next/router";
import { Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { selectCart, fetchCartItems } from "@/store/slices/cart";
import { selectWishlist, fetchWishlistItems } from "@/store/slices/wishlist";
import { selectAuth, authActions } from "@/store/slices/auth";
import { useAppDispatch } from "@/store";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { toast } from "react-toastify";
import { fbqTrackEvent } from "@/lib/fpixel";
import { unbxdCusEngTrackEvent } from "@/lib/unbxd";
import { GA4_ITEM_BRAND } from "@/lib/ga4";
import styles from "../../styles/shap_of_you.module.scss";

interface CartModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const CartModal: React.FC<CartModalProps> = ({ isOpen, setIsOpen }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const cartState = useSelector(selectCart);
  const wishlistState = useSelector(selectWishlist);
  const authState = useSelector(selectAuth);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [wishlistLoadingId, setWishlistLoadingId] = useState<number | null>(
    null
  );
  const [addOneMoreLoadingId, setAddOneMoreLoadingId] = useState<number | null>(
    null
  );

  const handleContinueShopping = () => {
    setIsOpen(false);
  };

  const handleProceedToCheckout = () => {
    setIsOpen(false);
    router.push("/cart");
  };

  const handleRemove = async (item: any) => {
    setRemovingId(item.id);
    try {
      await httpClient.delete(endpoints.cart.remove(item.id));
      await dispatch(fetchCartItems());
      toast.info("Item removed from cart");
        unbxdCusEngTrackEvent("Remove from cart ", {
          source: "web",
          platform: "senco",
          prid: item.data?.product_id,
          variant_id: item.identifier,
          image: item.product_image || item.image,
          prqt: 1,
          product_name: item.product?.title || item.product_name || item.name,
          amount:
            item.product_discounted_price || item.product_price || item.price,
          product_url: `${window.location.origin}/jewellery/${item.slug}`,
        });
    } catch {
      toast.error("Failed to remove item");
    } finally {
      setRemovingId(null);
    }
  };

  const handleAddOneMore = async (item: any) => {
    if (addOneMoreLoadingId === item.id) return;
    setAddOneMoreLoadingId(item.id);
    try {
      await httpClient.post(endpoints.cart.add(), {
        type: "product",
        identifier: item.identifier,
      });
      await dispatch(fetchCartItems());
      fbqTrackEvent("CustomizeProduct");
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: "add_to_cart",
        ecommerce: {
          currency: "INR",
          value: item.product_discounted_price || item.product_price || item.price || 0,
          items: [
            {
              item_id: item.data?.product_id || item.product?.sku || item.sku || "",
              item_name: item.product?.title || item.product_name || item.name,
              item_brand: GA4_ITEM_BRAND,
              item_category: item.product?.category || "",
              item_variant: item.data?.purity || item.product?.purity || item.product?.purities?.[0] || item.identifier,
              price: item.product_discounted_price || item.product_price || item.price || 0,
              quantity: 1,
            },
          ],
        },
      });
        unbxdCusEngTrackEvent("Add to cart ", {
          source: "web",
          platform: "senco",
          prid: item.data?.product_id,
          variant_id: item.identifier,
          image: item.product_image || item.image,
          prqt: 1,
          product_name: item.product?.title || item.product_name || item.name,
          amount:
            item.product_discounted_price || item.product_price || item.price,
          product_url: `${window.location.origin}/jewellery/${item.slug}`,
          discounted_price: item.product_discounted_price || 0,
          stock_availability: 1,
        });
    } catch (error: any) {
      if (error?.response?.data?.message) {
        toast(error.response.data.message, {
          type: "error",
          toastId: "add-one-more-error",
        });
      }
    }
    setAddOneMoreLoadingId(null);
  };

  const isInWishlist = (item: any) => {
    const productId = item.data?.product_id;
    return wishlistState.items.some((v) => v.id == productId);
  };

  const handleWishlist = async (item: any) => {
    if (wishlistLoadingId === item.id) return;
    if (!authState.user) {
      dispatch(authActions.setIsModalOpen(true));
      return;
    }
    const productId = item.data?.product_id;
    setWishlistLoadingId(item.id);
    try {
      if (isInWishlist(item)) {
        if (
          !confirm(
            "Are you sure you want to remove this product from wishlist?"
          )
        ) {
          setWishlistLoadingId(null);
          return;
        }
        await httpClient.post(endpoints.wishlist.remove(), {
          product_id: productId,
          user_id: authState.user.id,
        });
        await dispatch(fetchWishlistItems(authState.user.id as any));
        toast("Item removed from wishlist", {
          type: "success",
          toastId: "wishlist-remove-success",
        });
      } else {
        await httpClient.post(endpoints.wishlist.add(), {
          product_id: productId,
          user_id: authState.user.id,
        });
        await dispatch(fetchWishlistItems(authState.user.id as any));
        toast("Item added to wishlist", {
          type: "success",
          toastId: "wishlist-add-success",
        });
        if (window.smartech) {
          unbxdCusEngTrackEvent("Add to wishlist ", {
            source: "web",
            platform: "senco",
            prid: item.data?.product_id,
            variant_id: item.identifier,
            image: item.product_image || item.image,
            prqt: 1,
            product_name: item.product?.title || item.product_name || item.name,
            amount: item.product_price || item.price,
            product_url: `${window.location.origin}/jewellery/${item.slug}`,
            discounted_price: item.product_discounted_price || 0,
            stock_availability: 1,
          });
        }
      }
    } catch (error) {}
    setWishlistLoadingId(null);
  };

  return (
    <div className={styles.modal_pro_mobile_cart}>
      <div
        className={`${styles.overlay} ${isOpen ? styles.show : ""}`}
        onClick={() => setIsOpen(false)}
      >
        <button className={styles.closeBtn} onClick={() => setIsOpen(false)}>
          ✕
        </button>
        <div
          className={`${styles.modal} ${isOpen ? styles.open : ""}`}
          onClick={(e) => e.stopPropagation()}
          style={{ display: "flex", flexDirection: "column" }}
        >
          {/* Header */}
          <div className={styles.header}>
            <h2>Cart Page</h2>
          </div>

          <div className={styles.wrapper_card_desktop}>
            {cartState.items_loading &&
            removingId === null &&
            addOneMoreLoadingId === null ? (
              <div className={styles.loading}>
                <Spinner animation="border" role="status" />
              </div>
            ) : cartState.items.length === 0 ? (
              <div className={styles.empty}>
                <p>Your cart is empty</p>
              </div>
            ) : (
              cartState.items.map((item: any) => {
                return (
                  <div key={item.id} className={styles.item}>
                    <div className={styles.row}>
                      <div className={styles.imageBox}>
                        {item.product_image || item.image ? (
                          <img
                            src={item.product_image || item.image}
                            alt="product"
                          />
                        ) : (
                          <div className={styles.imagePlaceholder} />
                        )}
                      </div>

                      <div className={styles.details}>
                        <p className={styles.title}>
                          {item.product?.title ||
                            item.product_name ||
                            item.name}
                        </p>

                        {item.data.purity && (
                          <p className={styles.meta}>
                            Purity: <span>{item.data.purity}</span>
                          </p>
                        )}

                        {item.diamond_shape && (
                          <p className={styles.meta}>
                            Diamond Shape: <span>{item.diamond_shape}</span>
                          </p>
                        )}

                        <p className={styles.price}>
                          ₹{" "}
                          {(
                            item.product_discounted_price ||
                            item.product_price ||
                            item.price ||
                            0
                          ).toLocaleString("en-IN")}
                        </p>
                      </div>
                    </div>

                    {/* Actions row: wishlist | add one more | remove */}
                    <div className={styles.mobile_actions_row}>
                      {/* Wishlist — icon only */}
                      <button
                        className={styles.mobile_icon_btn}
                        onClick={() => handleWishlist(item)}
                        disabled={wishlistLoadingId === item.id}
                        title={
                          isInWishlist(item)
                            ? "Remove from Wishlist"
                            : "Add to Wishlist"
                        }
                      >
                        {wishlistLoadingId === item.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : isInWishlist(item) ? (
                          <i
                            className="bi bi-heart-fill"
                            style={{ color: "#FF1933" }}
                          />
                        ) : (
                          <i className="bi bi-heart" />
                        )}
                      </button>

                      {/* Add One More — icon + text (conditional) */}
                      {item.data?.is_limited_stock == 0 && (
                        <button
                          onClick={() => handleAddOneMore(item)}
                          disabled={addOneMoreLoadingId === item.id}
                          className={styles.mobile_add_btn}
                        >
                          {addOneMoreLoadingId === item.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <>
                              <i className="bi bi-plus-lg"></i> Add More
                            </>
                          )}
                        </button>
                      )}

                      {/* Remove — icon only */}
                      <button
                        onClick={() => handleRemove(item)}
                        disabled={removingId === item.id}
                        className={`${styles.mobile_icon_btn} ${styles.mobile_remove_btn}`}
                        title="Remove from cart"
                      >
                        {removingId === item.id ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="bi bi-trash" />
                        )}
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className={styles.footer1}>
            <button
              onClick={handleContinueShopping}
              className={`${styles.button} ${styles.continueBtn}`}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M19 12H5"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5L5 12L12 19"
                  stroke="#262121"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Continue Shopping
            </button>

            <button
              onClick={handleProceedToCheckout}
              className={`${styles.button} ${styles.checkoutBtn}`}
            >
              Proceed to Checkout
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 12H19"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5L19 12L12 19"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartModal;
